import { useState } from 'react';
import { dayIn, nextDay } from '@delivery/shared';
import { ApiFailure, api } from '../lib/api';

/**
 * 批量改「期望送达日期」。
 *
 * ★ 用户原话:「批量操作里面可以批量修改一些订单的预期达到时间」。
 *
 * ★ 为什么要有它:一车货临时改成明天送,挨个点进去改二十次的话他多半就不改了 ——
 *   而不改的后果是司机端第二天早上亮二十条「没送完」,那条提醒从此失效。
 *
 * ★ 摆两个快捷按钮(今天 / 明天)再给一个日期框,不是只给日期框:
 *   九成的操作就是这两个,而在手机上点日期选择器要三下。
 */
export function ExpectedDateDialog({
  orders,
  timezone,
  onClose,
  onDone,
}: {
  /** 选中的那几单 —— 要它们**当前**的日期来回填,不能只给 id */
  orders: Array<{ id: string; expectedDeliveryAt?: string | null }>;
  /** 店里的时区 —— 「今天」得按它算,不按这台电脑的 */
  timezone: string;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const orderIds = orders.map((o) => o.id);
  const today = dayIn(timezone);

  /*
   * ★ 打开时回填**这几单现在定在哪一天**,不是每次都重置成今天。
   *
   *   用户报的:「再次修改上面显示的还是当日的日期,没有显示现在最新的日期」。
   *   第一版就是写死 today 的 —— 于是他改成明天、再打开看到「今天」,
   *   以为没保存成功;而这时候再点一下「改」,就真的把它改回今天了。
   *   一个**会把人的修改悄悄撤销掉**的默认值,比没有默认值糟得多。
   *
   * ★ 选中的几单日期不一样时**不预选任何一天**:预选其中一个,
   *   点「改」就会把另外几单也拉过去,而他并没打算动那些。
   */
  const days = [
    ...new Set(
      orders.map((o) => (o.expectedDeliveryAt ? dayIn(timezone, new Date(o.expectedDeliveryAt)) : '')),
    ),
  ];
  const mixed = days.length > 1;
  const [date, setDate] = useState(mixed ? '' : (days[0] ?? today) || today);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ updated: number }>('/orders/batch-expected-delivery', {
        method: 'POST',
        body: { orderIds, date },
      });
      /*
       * ★ 改了几单要说,而且要和选了几单对比着说。
       *   已送达 / 已取消的单不给改(那几张的日期是历史),
       *   不说的话人以为全改了,而其中几单没动。
       */
      if (r.updated < orderIds.length) {
        setError(
          `改了 ${r.updated} 单;另外 ${orderIds.length - r.updated} 单没动 —— ` +
            '已送达、已退回、已取消的单不能改送达日期',
        );
        await onDone();
        return;
      }
      await onDone();
      onClose();
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : '改不动,稍后再试');
    } finally {
      setBusy(false);
    }
  }

  const QUICK: Array<[string, string]> = [
    [today, '今天'],
    [nextDay(today), '明天'],
  ];

  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-black/50 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="改送达日期"
        className="w-full max-w-sm rounded-2xl bg-white dark:bg-slate-800 p-5 shadow-2xl space-y-3"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
          改送达日期 · {orderIds.length} 单
        </h3>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          司机端只列出当天要送的单,过了这一天还没送完才会提醒他。
        </p>
        {mixed && (
          <p className="rounded-lg bg-slate-100 dark:bg-slate-700/50 px-3 py-2 text-xs
                        text-slate-600 dark:text-slate-300">
            选中的这几单现在定在不同的日期({days.filter(Boolean).join('、') || '有的还没定'})——
            选一天会把它们**全部**改成那一天。
          </p>
        )}

        <div className="flex gap-2">
          {QUICK.map(([v, label]) => (
            <button
              key={v}
              onClick={() => setDate(v)}
              className={`flex-1 h-10 rounded-lg border text-sm ${
                date === v
                  ? 'border-brand-600 bg-brand-50 text-brand-700 dark:bg-brand-950 dark:text-brand-300'
                  : 'border-slate-300 text-slate-700 dark:border-slate-600 dark:text-slate-300'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <label className="block">
          <span className="mb-1 block text-sm text-slate-600 dark:text-slate-400">或者选一天</span>
          <input
            type="date"
            aria-label="送达日期"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-600
                       bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 text-sm"
          />
        </label>

        {error && (
          <p className="rounded-lg bg-amber-50 dark:bg-amber-950/40 px-3 py-2 text-sm
                        text-amber-800 dark:text-amber-300">
            {error}
          </p>
        )}

        <div className="flex gap-2 pt-1">
          <button
            onClick={onClose}
            className="flex-1 h-11 rounded-lg border border-slate-300 dark:border-slate-600
                       text-slate-700 dark:text-slate-300"
          >
            取消
          </button>
          <button
            onClick={() => void save()}
            disabled={busy || !date}
            className="flex-1 h-11 rounded-lg bg-brand-600 text-white font-medium disabled:opacity-40"
          >
            {busy ? '处理中…' : '改'}
          </button>
        </div>
      </div>
    </div>
  );
}
